import { useDynamicConfig } from '@statsig/react-bindings';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';

import { useDiscountContext } from '@/app/[locale]/store/_contexts/DiscountProvider';

import getDiscountMessage from './discount/getDiscountMessage';
import showDiscountMessage from './discount/showDiscountMessage';
import type { UiDiscount } from './discount/types';
import useFormattedDiscountPreview from './useFormattedDiscountPreview';

export interface UseDiscountMessageProps {
  gen3WizardConsumer?: boolean;
  isAccessory: boolean;
}
interface UseDiscountMessage {
  displayMessage: string | null | ReactNode;
}

const useDiscountMessage = ({
  gen3WizardConsumer = false,
  isAccessory,
}: UseDiscountMessageProps): UseDiscountMessage => {
  const t = useTranslations();

  const sitewideDiscountConfig = useDynamicConfig('sitewide_discount');
  const sitewideDiscounts = (sitewideDiscountConfig.get('sitewideDiscount') ??
    '') as string | string[];
  const highestDiscountEyebrowProperties = sitewideDiscountConfig.get(
    'enableHighestDiscountEyebrow',
    {},
  ) as Record<string, boolean>;

  const enableHighestDiscountEyebrowForGen3 =
    gen3WizardConsumer && highestDiscountEyebrowProperties['oura-gen-3'];
  const enableHighestDiscountEyebrowForGen4 =
    !gen3WizardConsumer && highestDiscountEyebrowProperties['oura-ring-4'];
  const showHighestDiscountEyebrow =
    sitewideDiscounts.length > 0 &&
    (enableHighestDiscountEyebrowForGen3 ??
      enableHighestDiscountEyebrowForGen4);

  const discountConfig = useDynamicConfig('discount_user_interface');
  const statsigDiscountArray = discountConfig.get(
    'discountArray',
    [],
  ) as UiDiscount[];

  const {
    appliedDiscountCode,
    discount,
    discountIsLoading,
    discountIsSuccess,
    promotionCodes,
    urlDiscount,
    urlDiscountIsSuccess,
    urlPromotionData,
  } = useDiscountContext();

  // determine whether or not to show discount eyebrow
  const showUrlDiscount = showDiscountMessage({
    isAccessory,
    gen3WizardConsumer,
    urlDiscount,
    urlDiscountIsSuccess,
    urlPromotionData,
  });
  // get the appropriate discount message to display
  const discountMessage = getDiscountMessage({
    appliedDiscountCode,
    campaign: discount?.campaign,
    discount: showUrlDiscount ? urlDiscount : discount,
    discountIsLoading,
    discountIsSuccess,
    promotionCodes,
    showHighestDiscountEyebrow,
    showUrlDiscount,
    statsigDiscountArray,
    urlPromotionData,
  });

  // format price if applicable
  const { isLoading: formattedAmountIsLoading, formattedAmount } =
    useFormattedDiscountPreview(appliedDiscountCode);

  let displayMessage = null;

  // t() the Phrase string for display in multiple languages
  if (discountMessage) {
    if (!formattedAmountIsLoading) {
      displayMessage = t.rich(discountMessage, { amount: formattedAmount });
    } else {
      displayMessage = t(discountMessage);
    }
  }

  return { displayMessage };
};

export default useDiscountMessage;
